import React, { useState } from 'react';
import PropTypes from 'prop-types';
import copyToClipbord from 'copy-to-clipboard';
import pluralize from 'pluralize';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { numberWithCommas } from 'common/formatters';

import useBranch from 'client/hooks/useBranch';
import SrCard from 'client/components/SrCard/SrCard';
import SrSeparator from 'client/components/SrSeparator';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrLink from 'client/components/SrLink/SrLink';
import SrPropertySetupDocsItem from 'client/components/SrPropertySetupDocs/SrPropertySetupDocsItem';

import './SrPropertySetupDocs.less';

const SrPropertySetupDocs = ({ listing, match }) => {

  const { appUrl } = useBranch({
    appUrl: ['env', 'APP_URL'],
  });

  const [copied, setCopied] = useState(false);

  const handleCopyClick = () => {
    copyToClipbord(listing.description);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  const downloadPhotos = () => {
    const stringQuery = JSON.stringify({
      listingId: listing.id,
    });
    const encodedQuery = encodeURIComponent(stringQuery);
    const downloadDocumentsUrl = `${appUrl}/bundle-files/listing-photos?data=${encodedQuery}`;
    global.open(downloadDocumentsUrl);
  };

  const listingUrl = `/partner/${match.params.partnerSlug}/listing/${listing.id}/edit/property`;

  const downloadCMA = () => {
    const stringQuery = JSON.stringify({
      listingId: listing.id,
    });
    const encodedQuery = encodeURIComponent(stringQuery);
    const downloadDocumentsUrl = `${appUrl}/bundle-files/listing-cma?data=${encodedQuery}`;
    global.open(downloadDocumentsUrl);
  };

  const downloadBPO = () => {
    const stringQuery = JSON.stringify({
      listingId: listing.id,
    });
    const encodedQuery = encodeURIComponent(stringQuery);
    const downloadDocumentsUrl = `${appUrl}/bundle-files/listing-bpo?data=${encodedQuery}`;
    global.open(downloadDocumentsUrl);
  };

  return (
    <SrCard
      padding='20px'
    >
      <div
        styleName='itemsContainer'
      >
        <SrPropertySetupDocsItem
          icon='/images/propertySetupDocs/photos-icon.svg'
          title='Photos'
          subtitle={
            listing.photoUrls.length > 0
              ? `${listing.photoUrls.length} ${pluralize('photo', listing.photoUrls.length)}`
              : 'No photos'
          }
          downloadEnabled={listing.photoUrls.length > 0}
          handleDownload={downloadPhotos}
          editLink={`${listingUrl}?scrollTo=photos`}
        />
        <SrPropertySetupDocsItem
          icon='/images/propertySetupDocs/dollar-sign-outline.svg'
          title='CMA'
          subtitle={
            (listing.shouldRunCMA && listing.comparativeMarketAnalysisUpdated)
              ? `$${numberWithCommas(listing.comparativeMarketAnalysisRentPrice)} 
              | ${momentWithAustinTimezone(listing.comparativeMarketAnalysisUpdated).format('MMMM D, YYYY')}`
              : 'No CMA'
          }
          downloadEnabled={
            !!listing.comparativeMarketAnalysisPdfUrl
            && listing.shouldRunCMA
          }
          handleDownload={downloadCMA}
          editLink={`${listingUrl}?scrollTo=CMA`}
        />
        <SrPropertySetupDocsItem
          icon='/images/propertySetupDocs/bpo-icon.svg'
          title='BPO'
          subtitle={
            listing.brokerPriceOpinionPdfUrl
              ? 'Approved'
              : 'No BPO'
          }
          handleDownload={downloadBPO}
          editLink={`${listingUrl}?scrollTo=BPO`}
          downloadEnabled={!!listing.brokerPriceOpinionPdfUrl}
        />
      </div>
      <SrSeparator />
      <SrBox
        marginTop='33px'
      >
        <SrFlex
          justify='between'
        >
          <SrText
            font='greycliff'
            fontSize='21px'
            weight='800'
            letterSpacing='0'
          >
            Marketing Description
          </SrText>
          {
            listing.description && (
              <SrFlex
                cursor='pointer'
                onClick={handleCopyClick}
                align='center'
              >
                <SrBox
                  backgroundImage='url("/images/propertySetupDocs/copy-icon.svg")'
                  backgroundPosition='center'
                  backgroundRepeat='no-repeat'
                  width='16px'
                  height='16px'
                  marginRight='6px'
                />
                <div
                  styleName='copyText'
                >
                  {copied ? 'Copied' : 'Click to copy'}
                </div>
              </SrFlex>
            )
          }
        </SrFlex>
        {
          listing.description
            ? (
              <SrBox
                marginTop='15px'
                bgColor='grey-fill-light'
                borderRadius='10px'
                padding='16px'
              >
                <SrText
                  font='greycliff'
                  whiteSpace='prewrap'
                >
                  {listing.description}
                </SrText>
              </SrBox>
            )
            : (
              <SrLink to={`${listingUrl}?scrollTo=description`} className='editListingLink'>
                <SrBox
                  marginTop='15px'
                  bgColor='grey-fill-light'
                  borderRadius='10px'
                  padding='16px'
                >
                  <SrText
                    font='greycliff'
                    weight='700'
                    color='blue300'
                    cursor='pointer'
                  >
                    Edit description
                  </SrText>
                </SrBox>
              </SrLink>
            )
        }
      </SrBox>
    </SrCard>
  );
};

SrPropertySetupDocs.propTypes = {
  listing: PropTypes.object.isRequired,
};
export default SrPropertySetupDocs;
